"use client";

import { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { SCREENER_COLUMN_LABELS } from "./columns";
import {
  SCREENER_FIXED_COLUMNS,
  SCREENER_VIEW_PRESETS,
  type ScreenerScope,
  getScreenerDefaultOrder,
  normalizeScreenerOrder,
  normalizeScreenerVisibility,
} from "../utils/screener-preferences";

interface ScreenerColumnsSettingsModalProps {
  open: boolean;
  scope: ScreenerScope;
  columnOrder: string[];
  visibility: Record<string, boolean>;
  onOpenChange: (open: boolean) => void;
  onApply: (next: { columnOrder: string[]; visibility: Record<string, boolean> }) => void;
}

function SortableRow({
  id,
  label,
  visible,
  onToggle,
}: {
  id: string;
  label: string;
  visible: boolean;
  onToggle: (next: boolean) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled: SCREENER_FIXED_COLUMNS.includes(id as "company") });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex items-center gap-3 rounded-lg border border-border/50 bg-card px-3 py-2.5",
        isDragging && "z-10 opacity-90 shadow-md",
      )}
    >
      <button
        type="button"
        className="touch-none text-muted-foreground hover:text-foreground p-1 -m-1 rounded shrink-0"
        aria-label={`Reorder ${label}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Checkbox
        id={`screener-col-${id}`}
        checked={visible}
        onCheckedChange={(value) => onToggle(value === true)}
      />
      <Label htmlFor={`screener-col-${id}`} className="cursor-pointer truncate text-sm font-medium">
        {label}
      </Label>
    </div>
  );
}

export function ScreenerColumnsSettingsModal({
  open,
  scope,
  columnOrder,
  visibility,
  onOpenChange,
  onApply,
}: ScreenerColumnsSettingsModalProps) {
  const [localOrder, setLocalOrder] = useState<string[]>(columnOrder);
  const [localVisibility, setLocalVisibility] = useState<Record<string, boolean>>(visibility);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    if (!open) return;
    setLocalOrder(columnOrder);
    setLocalVisibility(visibility);
  }, [open, columnOrder, visibility]);

  const allColumns = useMemo(() => getScreenerDefaultOrder(scope), [scope]);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const movable = localOrder.filter((id) => !SCREENER_FIXED_COLUMNS.includes(id as "company"));
    const oldIndex = movable.indexOf(String(active.id));
    const newIndex = movable.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    setLocalOrder(["company", ...arrayMove(movable, oldIndex, newIndex)]);
  };

  const handlePreset = (presetId: string) => {
    const preset = SCREENER_VIEW_PRESETS[scope].find((item) => item.id === presetId);
    if (!preset) return;
    const nextVisibility = normalizeScreenerVisibility(
      scope,
      Object.fromEntries(
        allColumns
          .filter((id) => id !== "company")
          .map((id) => [id, preset.columns.includes(id)]),
      ),
    );
    setLocalVisibility(nextVisibility);
    setLocalOrder(normalizeScreenerOrder(scope, preset.columns));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="flex h-[85vh] max-h-[85vh] flex-col overflow-hidden p-0">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-3">
          <DialogTitle>Screener columns</DialogTitle>
          <DialogDescription>
            Toggle optional columns, drag to reorder, or start from a preset view.
          </DialogDescription>
        </DialogHeader>

        <div className="shrink-0 px-6 pb-4 flex flex-wrap gap-2">
          {SCREENER_VIEW_PRESETS[scope].map((preset) => (
            <Button
              key={preset.id}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handlePreset(preset.id)}
            >
              {preset.label}
            </Button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 space-y-2">
          <div className="rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5 text-sm font-medium text-muted-foreground">
            Company (fixed)
          </div>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext
              items={localOrder.filter((id) => id !== "company")}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {localOrder
                  .filter((id) => id !== "company")
                  .map((id) => (
                    <SortableRow
                      key={id}
                      id={id}
                      label={SCREENER_COLUMN_LABELS[id] ?? id}
                      visible={localVisibility[id] ?? false}
                      onToggle={(next) =>
                        setLocalVisibility((prev) => ({
                          ...prev,
                          [id]: next,
                        }))
                      }
                    />
                  ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        <DialogFooter className="shrink-0 border-t border-border/40 bg-background px-6 py-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              onApply({
                columnOrder: normalizeScreenerOrder(scope, localOrder),
                visibility: normalizeScreenerVisibility(scope, localVisibility),
              });
              onOpenChange(false);
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
